import Chip from "@/components/chip"
import Link from "@/components/link"
import Modal from "@/components/modal"
import { OverviewCard, SelectedScaleItem } from "@/components/WizardOverview"
import PreventionFunnel from "@/components/prevention-funnel"
import RiskScale from "@/components/risk-scale"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useRisksSpace } from "@/core/risks-space.context"
import { STATUS_SAVING } from "@/redux/_status.types"
import { useState } from "react"
import {
  calculateRps,
  evaluateRps,
  PopulationRpsOptions,
  RPS_FREQUENCY_SCALE,
  RPS_PERSONS_SCALE,
  RPS_PREVENTION_SCALE
} from "../../model/rps-evaluation"
import { RPS_RISK_LEVELS, RpsRiskAxis } from "../../model/rps-risk"
import populationStore from "../../model/population.store"
import { Population } from "../../model/population.types"
import RpsRiskEditModal from "./RpsRiskEditModal"
import RpsSituationsList from "../RpsSituationsList"

const STEPS = [
  {
    id: "SITUATIONS",
    label: "Situations de travail",
    description: "Situations à risque par thème",
    question: "Quelles situations de travail présentent un risque ?"
  },
  {
    id: "PERSONS",
    label: "Personnes concernées",
    description: "Part de la fonction concernée",
    question: "Combien de personnes sont en situation de RPS ?"
  },
  {
    id: "FREQUENCY",
    label: "Fréquence",
    description: "Exposition aux situations RPS",
    question: "À quelle fréquence les personnes sont-elles exposées aux RPS ?"
  },
  {
    id: "PREVENTION",
    label: "Prévention",
    description: "Mesures en place",
    question: "Quel est le niveau de prévention mis en place ?"
  },
  {
    id: "EVALUATION",
    label: "Évaluation",
    description: "Niveau de risque global",
    question: ""
  }
] as const

const STEP_KEYS: Partial<Record<string, "severity" | "frequency" | "prevention">> = {
  PERSONS: "severity",
  FREQUENCY: "frequency",
  PREVENTION: "prevention"
}

export default function RpsEvaluationEditModal(props: { population: Population; onClose: () => void }) {
  const { onClose } = props
  const dispatch = useAppDispatch()
  const { isReadonly } = useRisksSpace()
  //Version a jour du store : les situations sont enregistrees pendant l'evaluation
  const population =
    useAppSelector(populationStore.selectors.list).find((item) => item.id === props.population.id) ?? props.population
  const [options, setOptions] = useState<PopulationRpsOptions>(population.optionsRps)
  const [stepIndex, setStepIndex] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const step = STEPS[stepIndex]
  const isLastStep = stepIndex === STEPS.length - 1
  const [editingAxis, setEditingAxis] = useState<RpsRiskAxis | null>(null)
  const calculatedLevel = calculateRps(options).level

  //Critere saisi a l'etape courante, remis a "Non défini" depuis l'en-tete
  const stepKey = STEP_KEYS[step.id]

  function goTo(id: string) {
    setStepIndex(STEPS.findIndex((item) => item.id === id))
  }

  function edit(key: keyof PopulationRpsOptions, value: number | null) {
    setOptions((current) => ({ ...current, [key]: value }))
  }

  async function next() {
    if (!isLastStep) {
      setStepIndex(stepIndex + 1)
      return
    }
    setIsSaving(true)
    try {
      dispatch(populationStore.actions.edit({ key: "optionsRps", value: options, idInList: population.id }))
      await dispatch(populationStore.thunks.updateEntity()).unwrap()
      onClose()
    } catch {
      setIsSaving(false)
    }
  }

  if (isReadonly) {
    return (
      <Modal title="Risques psychosociaux" isFullscreen isCloseButtonVisible onClose={onClose}>
        <RpsEvaluationOverview population={population} options={options} />
      </Modal>
    )
  }

  return (
    <>
      <Modal
        title="Risques psychosociaux"
        wizardSteps={STEPS.map(({ id, label, description }) => ({ id, label, description }))}
        activeWizardStepId={step.id}
        onWizardStepClick={goTo}
        wizardOwlText={isLastStep ? undefined : step.question}
        isWizardBackVisible={stepIndex > 0}
        onClose={onClose}
        onNext={next}
        onNextCta={isLastStep ? "Enregistrer" : "Continuer"}
        nextDisabled={isSaving}
        status={isSaving ? STATUS_SAVING : undefined}>
        <div className="w-full max-w-[1100px] text-base">
          {/* Vue d'ensemble : directement les cartes, sans entete */}
          {!isLastStep && (
            <div className="mb-[16px] flex max-w-[780px] items-end justify-between gap-[16px]">
              <div>
                <div className="grey-t text-base font-bold mb-10">Fonction</div>
                <div className="text-lg">{population.name}</div>
              </div>
              {stepKey && options[stepKey] !== null && (
                <Link isWithoutMargin onClick={() => edit(stepKey, null)}>
                  Non défini
                </Link>
              )}
            </div>
          )}
          {step.id === "SITUATIONS" && (
            <section className="max-w-[780px]">
              <RpsSituationsList population={population} onAxisClick={setEditingAxis} />
            </section>
          )}

          {step.id === "PERSONS" && (
            <section className="max-w-[780px]">
              <RiskScale
                scale={RPS_PERSONS_SCALE}
                value={options.severity}
                onChange={(value) => edit("severity", value)}
              />
            </section>
          )}

          {step.id === "FREQUENCY" && (
            <section className="max-w-[780px]">
              <RiskScale
                scale={RPS_FREQUENCY_SCALE}
                value={options.frequency}
                onChange={(value) => edit("frequency", value)}
              />
            </section>
          )}

          {step.id === "PREVENTION" && (
            <section className="max-w-[780px]">
              <PreventionFunnel
                scale={RPS_PREVENTION_SCALE}
                value={options.prevention}
                onChange={(value) => edit("prevention", value)}
              />
            </section>
          )}

          {step.id === "EVALUATION" && (
            <RpsEvaluationOverview
              population={population}
              options={options}
              onLevelClick={(index) => edit("manualLevel", index === calculatedLevel ? null : index)}
              onStepClick={goTo}
            />
          )}
        </div>
      </Modal>
      {editingAxis && (
        <RpsRiskEditModal population={population} axis={editingAxis} onClose={() => setEditingAxis(null)} />
      )}
    </>
  )
}

//Vue d'ensemble : derniere etape du wizard, et seul affichage en lecture seule (sans clic)
function RpsEvaluationOverview({
  population,
  options,
  onLevelClick,
  onStepClick
}: {
  population: Population
  options: PopulationRpsOptions
  onLevelClick?: (index: number) => void
  onStepClick?: (id: string) => void
}) {
  const level = evaluateRps(options).level
  return (
    <div className="flex flex-col gap-20">
      <OverviewCard title={`Niveau de risque « ${population.name} »`}>
        <div className="flex flex-wrap gap-[8px] py-[4px]">
          {RPS_RISK_LEVELS.map((item, index) => (
            <Chip
              key={index}
              color={item.color}
              isOutlineColored={level !== index}
              onClick={onLevelClick ? () => onLevelClick(index) : undefined}>
              {item.label}
            </Chip>
          ))}
        </div>
      </OverviewCard>
      <OverviewCard title="Situations de travail" onClick={onStepClick && (() => onStepClick("SITUATIONS"))}>
        <RpsSituationsList population={population} isCompact />
      </OverviewCard>
      <OverviewCard title="Personnes concernées" onClick={onStepClick && (() => onStepClick("PERSONS"))}>
        <SelectedScaleItem scale={RPS_PERSONS_SCALE} value={options.severity} />
      </OverviewCard>
      <OverviewCard title="Fréquence" onClick={onStepClick && (() => onStepClick("FREQUENCY"))}>
        <SelectedScaleItem scale={RPS_FREQUENCY_SCALE} value={options.frequency} />
      </OverviewCard>
      <OverviewCard title="Prévention" onClick={onStepClick && (() => onStepClick("PREVENTION"))}>
        <SelectedScaleItem isPrevention scale={RPS_PREVENTION_SCALE} value={options.prevention} />
      </OverviewCard>
    </div>
  )
}
